// The Terrain radar, ported from the demo's radar option (scrips-demo-samer, Terrain view): a polygon with four rings, the
// "outside target" zone out to the 100 % ring, the "clinical target" zone, the previous draw as a dashed line and "now" as the
// primary line. Axis names are the system names: bold when the system needs attention or is selected, meta ink when nothing is
// on file, and a click on a name selects that system. Apache ECharts; every colour is a token read at build time.
import { useEffect, useRef } from 'react';
import * as echarts from 'echarts/core';
import { RadarChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import type { EChartsOption } from 'echarts';
import { CHART, SURFACE, TEXT } from '@/analytics-hub/chart/chart-tokens';
import type { TerrainSystemId } from './lib/terrain-catalog';
import type { SystemSummary } from './lib/terrain-model';

echarts.use([RadarChart, SVGRenderer]);

const HEIGHT = 340;

function cssVar(name: string): string {
  if (typeof document === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** The demo's zones: fill token, opacity, legend word (the legend draws the same tokens as classes). */
export const RADAR_ZONES = {
  outside: { token: '--color-yellow-200', opacity: 0.5 },
  target: { token: '--color-blue-200', opacity: 0.55 },
} as const;

const share = (n: number, d: number) => (d > 0 ? n / d : 0);

export function terrainRadarOption(systems: SystemSummary[], selected: TerrainSystemId | null): EChartsOption {
  const axes = systems.filter((s) => s.assessed > 0);
  const now = axes.map((s) => share(s.assessedWithin, s.assessed));
  // The demo's rule: the previous-draw line is drawn only when EVERY axis has a previous draw (a partial line would join
  // points across systems that were not measured then).
  const hasPrevious = axes.length > 2 && axes.every((s) => s.previousAssessed > 0);
  const zone = (key: keyof typeof RADAR_ZONES, value: number[]) => ({
    name: `zone:${key}`,
    value,
    symbol: 'none',
    lineStyle: { width: 0, opacity: 0 },
    areaStyle: { color: cssVar(RADAR_ZONES[key].token), opacity: RADAR_ZONES[key].opacity },
  });
  const ink = (s: SystemSummary) =>
    selected === s.system.id ? cssVar('--accent-active-text') : s.state === 'not-derived' ? TEXT.meta : TEXT.body;
  const byName = new Map(axes.map((s) => [s.system.short, s]));
  return {
    animationDuration: 400,
    tooltip: { show: false },
    legend: { show: false },
    radar: {
      center: ['50%', '52%'],
      radius: '64%',
      startAngle: 90,
      clockwise: true,
      splitNumber: 4,
      shape: 'polygon',
      axisNameGap: 10,
      triggerEvent: true,
      indicator: axes.map((s) => ({ name: s.system.short, max: 1, min: 0, color: ink(s) })),
      axisName: {
        fontSize: CHART.labelPx,
        formatter: (name?: string) => {
          const s = name ? byName.get(name) : undefined;
          return s && (selected === s.system.id || s.state === 'attention') ? `{strong|${name}}` : `{plain|${name}}`;
        },
        rich: { strong: { fontSize: CHART.labelPx, fontWeight: 600, color: 'inherit' }, plain: { fontSize: CHART.labelPx, fontWeight: 400, color: 'inherit' } },
      },
      splitLine: { lineStyle: { color: SURFACE.line, width: 1 } },
      splitArea: { show: false },
      axisLine: { lineStyle: { color: SURFACE.line, width: 1 } },
    },
    series: [
      {
        type: 'radar',
        silent: true,
        data:
          // The demo: zones and lines need a polygon (3+ axes). With 1 or 2 axes it still draws the frame and each axis's
          // point, so the radar never disappears while a system has an assessed marker.
          axes.length > 2
            ? [
                zone('outside', axes.map(() => 1)),
                zone('target', now),
                ...(hasPrevious
                  ? [{ name: 'previous', value: axes.map((s) => share(s.previousAssessedWithin, s.previousAssessed)), symbol: 'none', lineStyle: { color: TEXT.meta, width: 1.25, type: [4, 3] as number[] } }]
                  : []),
                { name: 'now', value: now, symbol: 'circle', symbolSize: 6, itemStyle: { color: CHART.series[0] }, lineStyle: { color: CHART.series[0], width: 2.25, join: 'round' as const } },
              ]
            : axes.length > 0
              ? [{ name: 'now', value: now, symbol: 'circle', symbolSize: 7, itemStyle: { color: CHART.series[0] }, lineStyle: { width: 0, opacity: 0 } }]
              : [],
      },
    ],
  };
}

export function TerrainRadarChart({
  systems,
  selected,
  onSelect,
  ariaLabel,
}: {
  systems: SystemSummary[];
  selected: TerrainSystemId | null;
  onSelect: (id: TerrainSystemId) => void;
  ariaLabel: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const chart = useRef<echarts.ECharts | null>(null);
  const select = useRef(onSelect);
  select.current = onSelect;
  const systemsRef = useRef(systems);
  systemsRef.current = systems;
  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const instance = echarts.init(el, undefined, { renderer: 'svg', height: HEIGHT });
    chart.current = instance;
    // A click on an axis name selects that system (the demo's axis names are the system switch).
    instance.on('click', (p: { componentType?: string; targetType?: string; name?: string }) => {
      if (p.componentType !== 'radar' || p.targetType !== 'axisName') return;
      const hit = systemsRef.current.find((s) => s.system.short === p.name);
      if (hit) select.current(hit.system.id);
    });
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => instance.resize({ height: HEIGHT }));
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      instance.dispose();
      chart.current = null;
    };
  }, []);
  useEffect(() => {
    chart.current?.setOption(terrainRadarOption(systems, selected), { notMerge: true, lazyUpdate: true });
  }, [systems, selected]);
  return <div ref={wrap} role="img" aria-label={ariaLabel} data-slot="terrain-radar" className="w-full" />;
}
